(() => {
  let queued = false;
  const mobileZoomStates = new WeakMap();

  const cleanText = (element) =>
    String(element?.getAttribute?.('aria-label') || element?.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();

  const normalizedWishlistLabel = (element) => {
    const text = cleanText(element).replace(/\s+\d+\s*$/, '').trim();
    if (/remove from wishlist/i.test(text)) return 'Remove from Wishlist';
    if (/added to wishlist/i.test(text)) return 'Added to Wishlist';
    return 'Add to Wishlist';
  };

  const directChild = (element, parent) => {
    let node = element;
    while (node?.parentElement && node.parentElement !== parent) {
      node = node.parentElement;
    }
    return node?.parentElement === parent ? node : null;
  };

  function makeDescriptionStatic() {
    const root = document.querySelector('#MainContent, main');
    if (!root) return;

    let details = [...root.querySelectorAll('details')].find((item) => {
      const summary = item.querySelector(':scope > summary');
      const label = String(summary?.textContent || '').replace(/\s+/g, ' ').trim();
      return /^Description\b/i.test(label);
    });

    if (!details) {
      const heading = [...root.querySelectorAll('summary, h2, h3, h4, [role="button"]')].find(
        (element) => String(element.textContent || '').replace(/\s+/g, ' ').trim() === 'Description'
      );
      details = heading?.closest?.('details') || null;
    }

    if (!details) return;

    const summary = details.querySelector(':scope > summary');
    if (!summary) return;

    details.classList.add('deliving-description-static');
    details.open = true;
    summary.setAttribute('aria-expanded', 'true');
    summary.setAttribute('tabindex', '-1');

    const iconCandidates = [
      ...summary.querySelectorAll(
        'svg, .svg-wrapper, [class*="chevron"], [class*="caret"], [class*="arrow"]'
      ),
    ].filter((element) => !element.closest('[class*="deliving-upi-headicon"]'));

    if (iconCandidates.length) {
      const rightmost = iconCandidates.reduce((winner, element) => {
        if (!winner) return element;
        return element.getBoundingClientRect().right > winner.getBoundingClientRect().right
          ? element
          : winner;
      }, null);
      rightmost?.classList.add('deliving-description-chevron');
    }

    if (!summary.dataset.delivingStaticDescription) {
      const preventToggle = (event) => {
        event.preventDefault();
        event.stopPropagation();
        details.open = true;
      };

      summary.addEventListener('click', preventToggle, true);
      summary.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Enter' || event.key === ' ') preventToggle(event);
        },
        true
      );
      details.addEventListener(
        'toggle',
        () => {
          if (!details.open) details.open = true;
        },
        true
      );
      summary.dataset.delivingStaticDescription = 'true';
    }
  }

  function findNativeWishlistControl() {
    const existing = document.querySelector(
      '.deliving-wishlist-native-control, .deliving-wishlist-original-control'
    );
    if (existing?.isConnected) return existing;

    const root = document.querySelector('#MainContent, main');
    if (!root) return null;

    const candidates = [
      ...root.querySelectorAll('button, a, [role="button"], input[type="button"]'),
    ];

    return (
      candidates.find((element) => {
        if (element.classList.contains('deliving-wishlist-inline')) return false;
        if (element.classList.contains('deliving-wishlist-visual-button')) return false;
        if (element.closest('.deliving-wishlist-inline')) return false;
        return /(add to|remove from|added to)\s+wishlist/i.test(cleanText(element));
      }) || null
    );
  }

  function chooseMovableWishlist(control, formButtons) {
    const candidates = [
      control.closest('[class*="wishlist-hero"], [class*="wishlist_hero"], [id*="wishlist"]'),
      control.closest('.shopify-app-block, .shopify-block'),
      control.parentElement,
      control,
    ];

    return (
      candidates.find((candidate) => {
        if (!(candidate instanceof HTMLElement)) return false;
        if (candidate === formButtons || candidate.matches('#MainContent, main')) return false;
        if (candidate.contains(formButtons)) return false;
        return true;
      }) || control
    );
  }

  function ensureWishlistWrapper(control, movable) {
    if (movable !== control) {
      movable.classList.remove('deliving-wishlist-original-hidden');
      movable.classList.add('deliving-wishlist-native-wrap');
      return movable;
    }

    const existingWrapper = control.parentElement?.closest('.deliving-wishlist-native-wrap');
    if (existingWrapper) return existingWrapper;

    const wrapper = document.createElement('div');
    wrapper.className = 'deliving-wishlist-native-wrap';
    control.insertAdjacentElement('beforebegin', wrapper);
    wrapper.appendChild(control);
    return wrapper;
  }

  function syncWishlistVisual(control, wrapper) {
    const label = normalizedWishlistLabel(control);
    const active = /remove from wishlist|added to wishlist/i.test(label);

    control.setAttribute('aria-label', label);
    wrapper.dataset.wishlistActive = active ? 'true' : 'false';

    let visualButton = wrapper.querySelector(':scope > .deliving-wishlist-visual-button');
    if (!(visualButton instanceof HTMLButtonElement)) {
      visualButton = document.createElement('button');
      visualButton.type = 'button';
      visualButton.className = 'deliving-wishlist-visual-button';
      visualButton.innerHTML = `
        <span class="visually-hidden">Add to Wishlist</span>
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"></path>
        </svg>`;
      wrapper.appendChild(visualButton);
    }

    visualButton.setAttribute('aria-label', label);
    const hiddenLabel = visualButton.querySelector('.visually-hidden');
    if (hiddenLabel) hiddenLabel.textContent = label;

    visualButton.onclick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (control.isConnected && !control.hasAttribute('disabled')) {
        control.click();
      }
      setTimeout(queueEnhance, 0);
      setTimeout(queueEnhance, 180);
      setTimeout(queueEnhance, 500);
    };
  }

  function enhanceWishlist() {
    document.querySelectorAll('.deliving-wishlist-inline').forEach((element) => element.remove());
    document
      .querySelectorAll('.deliving-wishlist-original-hidden')
      .forEach((element) => element.classList.remove('deliving-wishlist-original-hidden'));

    const formButtons = document.querySelector('.product-form-buttons');
    if (!formButtons) return;

    const addButton = formButtons.querySelector('.add-to-cart-button, button[name="add"]');
    if (!addButton) return;

    const cartCell = directChild(addButton, formButtons) || addButton;
    cartCell.classList.add('deliving-cart-cell');

    const control = findNativeWishlistControl();
    if (!control) return;

    control.classList.remove('deliving-wishlist-original-control');
    control.classList.add('deliving-wishlist-native-control');

    const movable =
      control.closest('.deliving-wishlist-native-wrap') || chooseMovableWishlist(control, formButtons);
    const wrapper = ensureWishlistWrapper(control, movable);

    syncWishlistVisual(control, wrapper);

    if (cartCell.nextElementSibling !== wrapper) {
      cartCell.insertAdjacentElement('afterend', wrapper);
    }

    formButtons.classList.add('deliving-product-controls-enhanced');
  }

  const distance = (a, b) => {
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.hypot(dx, dy);
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function navigateMobileZoom(wrapper, direction) {
    const current = wrapper.closest('.product-media-container');
    const gallery = current?.closest('.dialog-zoomed-gallery');
    const zoomDialog = wrapper.closest('zoom-dialog');
    if (!current || !gallery) return false;

    const items = [...gallery.children].filter(
      (item) => item instanceof HTMLElement && item.classList.contains('product-media-container')
    );
    const currentIndex = items.indexOf(current);
    if (currentIndex < 0) return false;

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= items.length) return false;

    mobileZoomStates.get(wrapper)?.reset?.();

    if (typeof zoomDialog?.selectThumbnail === 'function') {
      zoomDialog.selectThumbnail(nextIndex, { behavior: 'smooth' });
    } else {
      items[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    const targetWrapper = items[nextIndex]?.querySelector('drag-zoom-wrapper.product-media__drag-zoom-wrapper');
    if (targetWrapper) {
      requestAnimationFrame(() => mobileZoomStates.get(targetWrapper)?.reset?.());
    }

    return true;
  }

  function setupProfessionalMobileZoom(wrapper) {
    if (!(wrapper instanceof HTMLElement) || wrapper.dataset.delivingMobileZoom === 'true') return;

    const image = wrapper.querySelector('img.product-media__image, img');
    if (!(image instanceof HTMLImageElement)) return;

    wrapper.dataset.delivingMobileZoom = 'true';

    const state = {
      scale: 1,
      x: 0,
      y: 0,
      startScale: 1,
      startDistance: 0,
      startX: 0,
      startY: 0,
      startTranslateX: 0,
      startTranslateY: 0,
      touchStartedAt: 0,
      dragging: false,
      moved: false,
      pinching: false,
      lastTapTime: 0,
      lastTapX: 0,
      lastTapY: 0,
    };

    const constrain = () => {
      const rect = wrapper.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const naturalWidth = image.naturalWidth || rect.width;
      const naturalHeight = image.naturalHeight || rect.height;
      const fit = Math.min(rect.width / naturalWidth, rect.height / naturalHeight);
      const displayedWidth = naturalWidth * fit * state.scale;
      const displayedHeight = naturalHeight * fit * state.scale;
      const maxX = Math.max(0, (displayedWidth - rect.width) / 2 / state.scale);
      const maxY = Math.max(0, (displayedHeight - rect.height) / 2 / state.scale);

      state.x = clamp(state.x, -maxX, maxX);
      state.y = clamp(state.y, -maxY, maxY);
    };

    const apply = () => {
      constrain();
      wrapper.style.setProperty('--drag-zoom-scale', String(state.scale));
      wrapper.style.setProperty('--drag-zoom-translate-x', `${state.x}px`);
      wrapper.style.setProperty('--drag-zoom-translate-y', `${state.y}px`);
      wrapper.classList.toggle('deliving-mobile-zoomed', state.scale > 1.01);
    };

    const reset = () => {
      state.scale = 1;
      state.x = 0;
      state.y = 0;
      state.dragging = false;
      state.moved = false;
      state.pinching = false;
      state.startDistance = 0;
      apply();
    };

    const onTouchStart = (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      if (event.touches.length === 2) {
        const [first, second] = event.touches;
        if (!first || !second) return;
        state.startDistance = distance(first, second);
        state.startScale = state.scale;
        state.dragging = false;
        state.pinching = true;
        return;
      }

      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (!touch) return;

      const now = performance.now();
      const tapDistance = Math.hypot(touch.clientX - state.lastTapX, touch.clientY - state.lastTapY);
      if (now - state.lastTapTime < 300 && tapDistance < 45) {
        state.scale = state.scale > 1.01 ? 1 : 2;
        state.x = 0;
        state.y = 0;
        state.lastTapTime = 0;
        state.pinching = false;
        apply();
        return;
      }

      state.lastTapTime = now;
      state.lastTapX = touch.clientX;
      state.lastTapY = touch.clientY;
      state.startX = touch.clientX;
      state.startY = touch.clientY;
      state.startTranslateX = state.x;
      state.startTranslateY = state.y;
      state.touchStartedAt = now;
      state.dragging = state.scale > 1.01;
      state.moved = false;
      state.pinching = false;
    };

    const onTouchMove = (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      if (event.touches.length === 2) {
        const [first, second] = event.touches;
        if (!first || !second || !state.startDistance) return;
        state.pinching = true;
        state.scale = clamp((distance(first, second) / state.startDistance) * state.startScale, 1, 4);
        if (state.scale <= 1.01) {
          state.scale = 1;
          state.x = 0;
          state.y = 0;
        }
        apply();
        return;
      }

      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (!touch) return;

      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;
      if (Math.hypot(dx, dy) > 6) state.moved = true;

      if (!state.dragging) return;

      state.x = state.startTranslateX + dx / state.scale;
      state.y = state.startTranslateY + dy / state.scale;
      apply();
    };

    const onTouchEnd = (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      if (event.touches.length > 0) return;

      const endedTouch = event.changedTouches?.[0];
      const wasAtBaseZoom = state.scale <= 1.01;
      const wasPinching = state.pinching;
      state.dragging = false;
      state.pinching = false;

      if (endedTouch && wasAtBaseZoom && !wasPinching) {
        const dx = endedTouch.clientX - state.startX;
        const dy = endedTouch.clientY - state.startY;
        const elapsed = performance.now() - state.touchStartedAt;
        const horizontalSwipe = Math.abs(dx) >= 52 && Math.abs(dx) > Math.abs(dy) * 1.15;

        if (horizontalSwipe && elapsed <= 900) {
          const didNavigate = navigateMobileZoom(wrapper, dx < 0 ? 1 : -1);
          reset();
          if (didNavigate) return;
        }
      }

      if (state.scale <= 1.01) reset();
      else apply();
    };

    const suppressImageClick = (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    wrapper.addEventListener('touchstart', onTouchStart, { capture: true, passive: false });
    wrapper.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
    wrapper.addEventListener('touchend', onTouchEnd, { capture: true, passive: false });
    wrapper.addEventListener('click', suppressImageClick, true);
    window.addEventListener('resize', apply, { passive: true });

    const dialog = wrapper.closest('dialog.dialog-zoomed-gallery__dialog');
    if (dialog) {
      const openObserver = new MutationObserver(() => {
        if (dialog.open) requestAnimationFrame(reset);
      });
      openObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      dialog.addEventListener('close', () => requestAnimationFrame(reset));
    }

    mobileZoomStates.set(wrapper, { reset, apply });
    requestAnimationFrame(reset);
  }

  function enhanceMobileZoom() {
    document
      .querySelectorAll('dialog.dialog-zoomed-gallery__dialog drag-zoom-wrapper.product-media__drag-zoom-wrapper')
      .forEach(setupProfessionalMobileZoom);
  }

  function enhance() {
    if (!document.querySelector('.deliving-upi-headicon--delivery')) return;
    document.documentElement.classList.add('deliving-product-ui-active');
    makeDescriptionStatic();
    enhanceWishlist();
    enhanceMobileZoom();
  }

  function queueEnhance() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      enhance();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueEnhance, { once: true });
  } else {
    queueEnhance();
  }

  new MutationObserver(queueEnhance).observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
  });
})();
